import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { IUnscheduledAssignment, IUnscheduledAssignmentReview } from '../interface/UnscheduledAssignment';

const readError = (error: unknown, fallback: string): string => {
    if (error instanceof AxiosError) return error.response?.data?.message || fallback;
    if (error instanceof Error) return error.message || fallback;
    return fallback;
};

/**
 * The unscheduled assignments for one person over a date range, for the section
 * the timesheet form shows beside their scheduled hours.
 */
export const fetchUnscheduledAssignments = async (
    personId: number,
    startDate: string,
    endDate: string
): Promise<IUnscheduledAssignment[]> => {
    try {
        const response = await axiosInstance.get('/Personel/unscheduled-assignments', {
            params: { personId, startDate, endDate },
        });

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to fetch unscheduled assignments');
        }

        return response.data.data ?? [];
    } catch (error) {
        const message = readError(error, 'An unexpected error occurred while fetching unscheduled assignments.');
        toast.error(message);
        throw error;
    }
};

/**
 * Codes an entry onto the timesheet.
 *
 * This is the step that pays it: the server writes a one-day additional
 * schedule and a timesheet row, and the hours appear in the Additional Hours
 * table from then on. The server stamps who did it from their token.
 */
export const reviewUnscheduledAssignment = async (
    id: number,
    review: IUnscheduledAssignmentReview
): Promise<IUnscheduledAssignment | null> => {
    try {
        const response = await axiosInstance.put(`/Personel/unscheduled-assignments/${id}/review`, review);

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to code the entry');
        }

        toast.success(response.data.message || 'Coded onto the timesheet');
        return response.data.data ?? null;
    } catch (error) {
        const message = readError(error, 'An unexpected error occurred while coding the entry.');
        toast.error(message);
        throw error;
    }
};
